import { CloseOutlined, DeleteOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useCartDrawer } from '@contexts';
import { useAuth, useCart, useOrders } from '@hooks';
import { Button, Drawer, Empty, InputNumber, Spin, Typography } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

export const CartDrawer = () => {
  const { drawerOpen, closeDrawer } = useCartDrawer();
  const { profile: { data: profile } } = useAuth();
  const isAuthenticated = !!profile;
  const { cart, isLoading, updateQuantity, removeFromCart, clearCart } = useCart(isAuthenticated);
  const { createCheckoutSession } = useOrders();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getProductImage = (imageUrl: string, productName: string) => {
    if (imageUrl && imageUrl.startsWith('http')) {
      return imageUrl;
    } else if (imageUrl) {
      const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'ruppert-production';
      const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
      return `https://${bucketName}.s3.${region}.amazonaws.com/${imageUrl}`;
    }
    return `https://placehold.co/100x100/1a1a1a/E3AF25?text=${encodeURIComponent(productName)}`;
  };

  const handleQuantityChange = async (cartItemId: string, newQuantity: number) => {
    try {
      await updateQuantity(cartItemId, newQuantity);
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleRemoveItem = async (cartItemId: string) => {
    try {
      await removeFromCart(cartItemId);
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      closeDrawer();
      navigate('/login');
      return;
    }

    if (!cart || cart.items.length === 0) {
      return;
    }

    setCheckingOut(true);
    try {
      await createCheckoutSession.mutateAsync({
        lineItems: cart.items.map(item => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity,
        })),
        successUrl: `${window.location.origin}/order-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/merch`,
      });

      // Clear cart after successful checkout session creation
      await clearCart();
      closeDrawer();
    } catch (error) {
      console.error('Checkout error:', error);
      setCheckingOut(false);
    }
  };

  return (
    <Drawer
      title={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Title level={3} className="font-android !text-white mb-0 uppercase tracking-wider !font-normal !leading-none pt-2.5">
              Shopping Cart
            </Title>
          </div>
          <span className="text-yellow-principal font-android text-lg uppercase pt-1">
            {cart?.totalItems || 0} {cart?.totalItems === 1 ? 'ITEM' : 'ITEMS'}
          </span>
        </div>
      }
      placement="right"
      onClose={closeDrawer}
      open={drawerOpen}
      width={450}
      closeIcon={<CloseOutlined className="text-yellow-principal text-lg" />}
      className="cart-drawer"
      styles={{
        body: {
          backgroundColor: '#0a0a0a',
          padding: 0,
        },
        header: {
          backgroundColor: '#0a0a0a',
          borderBottom: '2px solid #E3AF25',
        },
      }}
    >
      {isLoading ? (
        <div className="flex justify-center items-center h-full bg-zinc-900">
          <Spin size="large" />
        </div>
      ) : !cart || cart.items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full px-6 bg-zinc-900">
          <Empty
            image={<ShoppingCartOutlined style={{ fontSize: 80, color: '#E3AF25' }} />}
            imageStyle={{ height: 80 }}
            description={
              <Paragraph className="!text-gray-300 font-barlow text-center text-lg">
                Your cart is empty
              </Paragraph>
            }
          >
            <Button
              type="primary"
              onClick={() => {
                closeDrawer();
                navigate('/merch');
              }}
              className="bg-yellow-principal hover:bg-yellow-600 border-none font-android uppercase tracking-wider text-black hover-3d-no-shadow"
            >
              BROWSE PRODUCTS
            </Button>
          </Empty>
        </div>
      ) : (
        <div className="flex flex-col h-full bg-zinc-900">
          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {cart.items.map((item) => (
              <div
                key={item.id}
                className="flex gap-4 py-4 border-b border-zinc-700 last:border-0"
              >
                <img
                  src={getProductImage(item.productImage, item.productName)}
                  alt={item.productName}
                  className="w-20 h-20 object-cover rounded border border-zinc-600"
                />
                <div className="flex-1">
                  <Text className="!text-white font-android block mb-1 text-base">
                    {item.productName}
                  </Text>
                  {item.size && (
                    <Text className="text-gray-400 font-barlow block mb-2">
                      Size: {item.size}
                    </Text>
                  )}
                  <Text className="!text-gray-400 font-barlow block mb-2">
                    {formatPrice(item.productPrice)} each
                  </Text>
                  <div className="flex items-center gap-2">
                    <InputNumber
                      min={1}
                      value={item.quantity}
                      onChange={(value) => handleQuantityChange(item.id, value || 1)}
                      className="w-20 cart-input-number !rounded-none"
                      size="small"
                      controls={false}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined style={{ color: '#ef4444' }} />}
                      onClick={() => handleRemoveItem(item.id)}
                      size="small"
                      className="cart-delete-button"
                    />
                  </div>
                </div>
                <div className="text-right">
                  <Text className="text-yellow-principal font-android !font-normal text-lg tracking-wider">
                    {formatPrice(item.subtotal)}
                  </Text>
                </div>
              </div>
            ))}
          </div>

          {/* Footer with Total and Actions */}
          <div className="border-t-2 border-yellow-principal px-6 py-5 bg-zinc-800">
            <div className="flex justify-between items-baseline mb-4">
              <Text className="!text-white font-android uppercase tracking-wider text-lg">TOTAL:</Text>
              <Title level={3} className="text-yellow-principal font-android !font-normal mb-0 text-2xl tracking-wider">
                {formatPrice(cart.totalAmount)}
              </Title>
            </div>

            <div className="space-y-3">
              <Button
                type="primary"
                block
                size="large"
                onClick={handleCheckout}
                loading={checkingOut}
                disabled={checkingOut || !cart || cart.items.length === 0}
                className="bg-yellow-principal hover:bg-yellow-600 border-none font-android uppercase tracking-wider h-12 text-black text-base hover-3d-no-shadow"
                icon={<ShoppingCartOutlined />}
              >
                {isAuthenticated ? 'CHECKOUT' : 'LOGIN TO CHECKOUT'}
              </Button>

              <Button
                type="default"
                block
                onClick={handleClearCart}
                disabled={!cart || cart.items.length === 0}
                className="font-android uppercase tracking-wider bg-transparent border-none !text-red-400 hover-3d-no-shadow cart-clear-button"
              >
                CLEAR CART
              </Button>
            </div>

            {!isAuthenticated && (
              <Paragraph className="!text-gray-400 text-sm text-center mt-3 mb-0 font-barlow">
                Login to save your cart and checkout
              </Paragraph>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};
